import React, { forwardRef } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import TagWrapper from '../TagWrapper';
import Icon from '../Icon';

export const ButtonGroup = forwardRef(
  ({ children, className, isToolbar, isVertical, size, ariaLabel, ...props }, ref) => {
    const PREFIX = isToolbar ? 'toolbar' : 'group';
    return (
      <div
        ref={ref}
        className={classNames(
          {
            [`btn-${PREFIX}`]: !isVertical,
            'btn-group-vertical': isVertical && PREFIX === 'group',
            [`btn-group-${size}`]: size,
          },
          className,
        )}
        role={PREFIX}
        aria-label={ariaLabel}
        {...props}
      >
        {children}
      </div>
    );
  },
);

const Button = forwardRef(
  (
    {
      children,
      tag = 'button',
      type = 'button',
      to,
      href,
      isActive,
      color,
      isOutline,
      isLight,
      isLink,
      className,
      icon,
      rounded,
      size,
      isDisable,
      shadow,
      hoverShadow,
      target,
      isVisuallyHidden,
      ...props
    },
    ref,
  ) => {
    const BTN_CLASS = classNames(
      'btn',
      {
        [`btn-${isOutline || isLink ? `outline-${color}` : color}`]:
          (color && !isLight) || (color && isLink),
        'border-transparent': isLink,
        [`btn-${size}`]: size,
        [`btn-hover-shadow${hoverShadow !== 'default' ? `-${hoverShadow}` : ''}`]:
          hoverShadow,
        [`btn-light-${color}`]: isLight,
        [`shadow${shadow !== 'default' ? `-${shadow}` : ''}`]: !!shadow,
        [`rounded${rounded !== 'default' ? `-${rounded}` : ''}`]: rounded,
        'rounded-0':
          rounded === 'bottom' ||
          rounded === 'top' ||
          rounded === 'end' ||
          rounded === 'start' ||
          rounded === 0,
        'btn-only-icon': !children || isVisuallyHidden,
        disabled: isDisable,
        active: isActive,
      },
      className,
    );

    const INNER = (
      <>
        {icon && <Icon iconName={icon} className="btn-icon" />}
        {isVisuallyHidden ? (
          <span className="visually-hidden">Toggle Dropdown</span>
        ) : (
          children
        )}
      </>
    );

    const ANCHOR_LINK_PATTERN = /^#/i;

    const disableProps = isDisable && {
      tabIndex: -1,
      'aria-disabled': true,
      disabled: true,
    };

    if (tag === 'a') {
      if (typeof to === 'string' && ANCHOR_LINK_PATTERN.test(to)) {
        return (
          <HashLink ref={ref} className={BTN_CLASS} to={to} {...disableProps} {...props}>
            {INNER}
          </HashLink>
        );
      }
      if (to) {
        return (
          <Link
            ref={ref}
            className={BTN_CLASS}
            to={to}
            rel="noopener"
            target={target}
            {...disableProps}
            {...props}
          >
            {INNER}
          </Link>
        );
      }
      return (
        <a
          ref={ref}
          className={BTN_CLASS}
          href={href}
          role="button"
          rel="noopener"
          target={target}
          {...disableProps}
          {...props}
        >
          {INNER}
        </a>
      );
    }

    return (
      <TagWrapper
        ref={ref}
        tag={tag}
        type={type}
        className={BTN_CLASS}
        {...disableProps}
        {...props}
      >
        {INNER}
      </TagWrapper>
    );
  },
);

export default Button;
